import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { LoadingPage } from '@/components/common/Loading';
import { Building2, MapPin, Users, Calendar, Star, ChevronRight } from 'lucide-react';

const API_BASE = '';
const FLAG_MAP = { 'USA': 'https://media.api-sports.io/flags/us.svg', 'Canada': 'https://media.api-sports.io/flags/ca.svg', 'Mexico': 'https://media.api-sports.io/flags/mx.svg' };
const STADIUM_IMG = 'https://static.prod-images.emergentagent.com/jobs/2b79e76a-42eb-4df3-ab27-72c6bfd04714/images/e9979bf9f1535ee42b01c32e789dfde52741353f493f4572e0b23ec8b78a25c7.png';

export default function WorldCupStadiums() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;

  useEffect(() => {
    fetch(`${API_BASE}/api/worldcup/venues`).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  return (
    <div data-testid="wc-stadiums-page">
      <WCSubmenu />
      <SEOHead title={t('wc.stadiumsTitle') + ' - ' + t('wc.breadcrumb') + ' | ODDSIS'} />

      {/* Hero with stadium image */}
      <div className="relative overflow-hidden h-48 md:h-56">
        <img src={STADIUM_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 z-10 container mx-auto px-4 max-w-7xl pb-6">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Building2 className="w-8 h-8 inline mr-3 text-amber-400" />{t('wc.stadiumsTitle').toUpperCase()}
          </h1>
          <p className="text-sm text-slate-300">{data.stadiums?.length} {t('wc.stadiums').toLowerCase()} - 3 {t('wc.countriesLabel')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.stadiums?.map(stadium => (
            <Link key={stadium.slug || stadium.name} to={`${wcBase}/${currentLang === "tr" ? "stadyum" : "stadium"}/${stadium.slug}`}
              data-testid={`stadium-link-${stadium.slug}`}
              className="group bg-slate-900/50 backdrop-blur-md border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/40 transition-all duration-300 hover:shadow-[0_8px_32px_rgba(245,158,11,0.1)] hover:-translate-y-0.5">
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <img src={FLAG_MAP[stadium.country]} alt="" className="w-5 h-3.5 rounded-sm" />
                      <h3 className="text-base font-bold text-white group-hover:text-amber-300 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>{stadium.name}</h3>
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{stadium.display_city || stadium.city}, {stadium.state}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {stadium.hosts_final && (
                      <span className="flex items-center gap-1 text-[10px] bg-amber-500/20 text-amber-400 px-2.5 py-1 rounded-full font-bold uppercase tracking-wider">
                        <Star className="w-3 h-3" />{t('wc.finalLabel')}
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-400 transition-colors" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <Users className="w-4 h-4 mx-auto text-amber-400/60 mb-1" />
                    <p className="text-base font-black text-white tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{(stadium.capacity || 0).toLocaleString()}</p>
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider">{t('wc.capacity')}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <Calendar className="w-4 h-4 mx-auto text-amber-400/60 mb-1" />
                    <p className="text-base font-black text-white tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{stadium.opened}</p>
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider">{t('wc.opened')}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <Star className="w-4 h-4 mx-auto text-amber-400/60 mb-1" />
                    <p className="text-base font-black text-white tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{stadium.wc_matches || stadium.matches}</p>
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider">{t('wc.matches')}</p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
